import {debounce} from '@github/mini-throttle'
import {PlaygroundChatInput} from './PlaygroundChatInput'
import {PlaygroundChatMessage} from './PlaygroundChatMessage'
import {useEffect, useCallback, useRef, useState} from 'react'
import {
  Panel,
  SidebarSelectionOptions,
  usePlaygroundManager,
  usePlaygroundState,
  type ModelState,
} from '../../../utils/playground-manager'
import {
  ActionList,
  ActionMenu,
  Box,
  Button,
  IconButton,
  SegmentedControl,
  Text,
  useResponsiveValue,
} from '@primer/react'

import {
  getSavedPlaygroundMessages,
  setPlaygroundMessages,
  type StoredChatMessages,
} from '../../../utils/playground-local-storage'
import type {PlaygroundMessage} from '../../../../types'
import {ModelLegalTerms} from './GettingStartedDialog/ModelLegalTerms'
import {SidebarExpandIcon, SlidersIcon, InfoIcon, HistoryIcon, PlusIcon, TrashIcon} from '@primer/octicons-react'
import {PlaygroundCodeLanguage} from './PlaygroundCodeLanguage'
import PlayGroundChatEmptyState from './PlaygroundChatEmptyState'
import {PlaygroundCodeSDK} from './PlaygroundCodeSDK'
import {useNavigate} from '@github-ui/use-navigate'
import {useLocation} from 'react-router-dom'
import {ModelUrlHelper} from '../../../utils/model-url-helper'
import {PlaygroundCodeSnippet} from './PlaygroundCodeSnippet'
import {PlaygroundJSON} from './PlaygroundJSON'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import ModelInfo from './ModelInfo'
import ModelParameters from './ModelParameters'
import {textFromMessageContent} from '../../../utils/message-content-helper'

enum PlaygroundContentOption {
  CHAT = 0,
  CODE = 1,
  JSON = 2,
}

const RestoreHistoryButton = ({handleRestoreHistory}: {handleRestoreHistory: () => void}) => {
  return (
    <Box className="position-sticky d-flex flex-justify-end" sx={{top: '1rem', marginBottom: '-2rem', zIndex: 1}}>
      <IconButton
        data-testid="restore-history-button"
        icon={HistoryIcon}
        size="small"
        onClick={handleRestoreHistory}
        sx={{display: ['flex', 'none', 'none']}}
        aria-label="Restore last session"
      />
      <Button size="small" onClick={handleRestoreHistory} sx={{display: ['none', 'flex', 'flex']}}>
        Restore last session
      </Button>
    </Box>
  )
}

const AddModel = ({
  playgroundState,
  handleAddModel,
}: {
  playgroundState: ReturnType<typeof usePlaygroundState>
  handleAddModel: () => void
}) => {
  const isSendingMessage = playgroundState.models.some(m => m.isLoading)
  return (
    <Box
      className="position-sticky flex-justify-end"
      sx={{
        top: '1rem',
        marginBottom: '-2rem',
        zIndex: 1,
        display: ['none', 'flex', 'flex'],
      }}
    >
      <Button
        leadingVisual={PlusIcon}
        onClick={handleAddModel}
        size="small"
        disabled={isSendingMessage}
        sx={{'@media screen and (max-width: 1024px)': {display: 'none'}}}
      >
        Compare
      </Button>
      <IconButton
        icon={PlusIcon}
        size="small"
        aria-label="Add model to compare"
        onClick={handleAddModel}
        disabled={isSendingMessage}
        sx={{'@media screen and (min-width: 1025px)': {display: 'none'}}}
      />
    </Box>
  )
}

export function PlaygroundChat({model, position}: {model: ModelState; position: number}) {
  const playgroundState = usePlaygroundState()
  const {showParameters, iceBreakerCheckPending} = playgroundState
  const {messages, isLoading, catalogData, parametersHasChanges} = model
  const lastIndex = messages.length - 1
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const scrolledToBottomRef = useRef(true)
  const manager = usePlaygroundManager()
  const [history, setHistory] = useState<PlaygroundMessage[]>([])
  const isMobile = useResponsiveValue({narrow: true}, false)
  const navigate = useNavigate()

  const [toolbarContent, setToolbarContent] = useState<React.ReactNode | null>(null)
  const [fullWidthToolbarContent, setFullWidthToolbarContent] = useState<React.ReactNode | null>(null)

  const lastPathComponent = useLocation().pathname.split('/').pop()?.toLowerCase()

  const comparisonFeatureFlag = useFeatureFlag('project_neutron_comparison_view')

  const [selectedOption, setSelectedOption] = useState<number>(() => {
    if (lastPathComponent === ModelUrlHelper.chatSuffix) {
      return PlaygroundContentOption.CHAT
    }
    if (lastPathComponent === ModelUrlHelper.codeSuffix) {
      return PlaygroundContentOption.CODE
    }
    if (lastPathComponent === ModelUrlHelper.jsonSuffix) {
      return PlaygroundContentOption.JSON
    }

    return PlaygroundContentOption.CHAT
  })

  const canRestoreHistory = history.length > 0 && messages.length === 0 && playgroundState.models.length === 1
  const isComparable = comparisonFeatureFlag && playgroundState.models.length === 1
  const onComparisonMode = playgroundState.models.length > 1

  const scrollToBottom = useCallback(() => {
    const forceScroll = false // Might be needed later

    function isScrollable(element: HTMLElement): boolean {
      if (!element.scrollTo) return false // This happens in the jest tests on CI
      return window.getComputedStyle(element).overflowY === 'auto'
    }

    if (!messagesContainerRef.current) return // This is not expected
    if (!scrolledToBottomRef.current && !forceScroll) return // The user has scrolled up, let's not scroll them back down

    // Depending on the screen size and the mode, we might either have a scroll
    // container around the messages, or we might use the window scroll.
    if (isScrollable(messagesContainerRef.current)) {
      messagesContainerRef.current.scrollTo(0, messagesContainerRef.current.scrollHeight)
    } else {
      window.scrollTo(0, document.body.scrollHeight)
    }
  }, [])

  useEffect(() => {
    if (onComparisonMode) return
    const savedMessages = getSavedPlaygroundMessages()
    const chatHistory = savedMessages && savedMessages.modelName === catalogData.name ? savedMessages.messages : []
    setHistory(chatHistory)
  }, [catalogData.name, onComparisonMode])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLElement
    // This calculation can be finnicky with fractional pixels. If we're within 1px of the bottom, we are close enough.
    scrolledToBottomRef.current = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSetPlaygroundMessages = useCallback(
    debounce((newMessages: StoredChatMessages) => {
      setPlaygroundMessages(newMessages)
    }, 200),
    [],
  )

  useEffect(() => {
    if (position !== Panel.Main) return
    if (messages.length > 0) {
      const chatMessages = {
        modelName: catalogData.name,
        messages,
      }
      debounceSetPlaygroundMessages(chatMessages)
    }
  }, [messages, debounceSetPlaygroundMessages, catalogData.name, position])

  const handleClearHistory = () => {
    manager.resetHistory(position, true)
    setHistory([])
  }

  const handleRestoreHistory = () => {
    manager.setMessages(position, history)
  }

  const handleOptionChange = (option: PlaygroundContentOption) => {
    setSelectedOption(option)
    if (position !== Panel.Main) return
    switch (option) {
      case PlaygroundContentOption.CHAT:
        navigate(ModelUrlHelper.addCompareParamsToUrl(ModelUrlHelper.playgroundUrl(catalogData)))
        break
      case PlaygroundContentOption.CODE:
        navigate(ModelUrlHelper.addCompareParamsToUrl(ModelUrlHelper.playgroundCodeUrl(catalogData)))
        break
      case PlaygroundContentOption.JSON:
        navigate(ModelUrlHelper.addCompareParamsToUrl(ModelUrlHelper.playgroundJsonUrl(catalogData)))
        break
    }
  }

  const smallSizeForSegmentControl = {
    height: '28px',
    fontSize: 1,
  }

  const handleRegenerate = useCallback(
    (index: number) => {
      if (index > messages.length) return
      const isLastResponse = index + 1 === messages.length
      if (!isLastResponse) return
      const messageContent = messages && messages[index - 1]?.message
      if (!messageContent) return
      const text = textFromMessageContent(messageContent)
      if (!text) return
      let attachment = null
      if (typeof messageContent === 'object' && messageContent[0].type === 'image_url') {
        attachment = messageContent[0].image_url.url
      }

      // Remove the latest message and response
      const filteredMessages = messages.slice(0, -2)
      const newModelState = {...model, messages: filteredMessages}
      manager.sendMessage(position, newModelState, text, attachment)
    },
    [manager, messages, model, position],
  )
  const handleAddModel = () => {
    const currentModelName = catalogData.name
    if (!currentModelName) return
    manager.getSideModel(currentModelName)
  }

  const renderToolbar = (option: PlaygroundContentOption) => {
    switch (option) {
      case PlaygroundContentOption.CHAT:
        return (
          <>
            {canRestoreHistory ? <RestoreHistoryButton handleRestoreHistory={handleRestoreHistory} /> : null}
            {isComparable && !isMobile && (
              <AddModel playgroundState={playgroundState} handleAddModel={handleAddModel} />
            )}
            <IconButton
              icon={TrashIcon}
              size="small"
              aria-label="Reset chat history"
              disabled={messages.length === 0}
              onClick={handleClearHistory}
            />
          </>
        )
      case PlaygroundContentOption.CODE: {
        return (
          <>
            <PlaygroundCodeLanguage gettingStarted={model.gettingStarted} />
            <PlaygroundCodeSDK gettingStarted={model.gettingStarted} />
          </>
        )
      }
      case PlaygroundContentOption.JSON: {
        return (
          <>
            {canRestoreHistory ? <RestoreHistoryButton handleRestoreHistory={handleRestoreHistory} /> : null}
            {toolbarContent}
          </>
        )
      }
    }
  }

  const renderOption = (option: PlaygroundContentOption) => {
    switch (option) {
      case PlaygroundContentOption.CHAT:
        return (
          <div className="pl-3 pb-2 overflow-auto flex-1 d-flex flex-column">
            <div
              className="flex-1 d-flex flex-column flex-justify-between pr-3 overflow-auto"
              ref={messagesContainerRef}
              onScroll={handleScroll}
            >
              <div className="flex-1 position-relative pt-3">
                {messages.length > 0 ? (
                  <div className="position-absolute">
                    {messages.map((message, index) => (
                      <PlaygroundChatMessage
                        model={model}
                        message={message}
                        index={index}
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={`${index}-${message.timestamp}`}
                        isLoading={index === lastIndex && isLoading}
                        isError={message.role === 'error'}
                        lastIndex={index === lastIndex}
                        handleRegenerate={handleRegenerate}
                        position={position}
                      />
                    ))}
                  </div>
                ) : (
                  !iceBreakerCheckPending && (
                    <PlayGroundChatEmptyState
                      model={model}
                      submitMessage={content => {
                        manager.sendChatMessage(playgroundState, content)
                      }}
                    />
                  )
                )}
              </div>
            </div>
            <PlaygroundChatInput model={model} position={position} />
            <div className="pt-3 pb-2 d-flex flex-justify-center pr-3">
              <ModelLegalTerms modelName={catalogData.name} />
            </div>
          </div>
        )
      case PlaygroundContentOption.CODE: {
        return <PlaygroundCodeSnippet model={model} />
      }
      case PlaygroundContentOption.JSON: {
        return (
          <PlaygroundJSON
            setFullWidthToolbarContent={setFullWidthToolbarContent}
            setToolbarContent={setToolbarContent}
            model={model}
            position={position}
          />
        )
      }
    }
  }

  return (
    <div className="flex-1 d-flex flex-column min-width-0 height-full">
      {fullWidthToolbarContent ? (
        <Box
          sx={{
            backgroundColor: 'canvas.subtle',
            gap: 2,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            borderBottomColor: 'border.default',
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          }}
        >
          {fullWidthToolbarContent}
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'canvas.subtle',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            borderBottomColor: 'border.default',
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          }}
        >
          <SegmentedControl
            aria-label={selectedOption === PlaygroundContentOption.CHAT ? 'Show playground chat' : 'Show code sample'}
            sx={smallSizeForSegmentControl}
            size="small"
            onChange={handleOptionChange}
          >
            <SegmentedControl.Button
              sx={smallSizeForSegmentControl}
              selected={selectedOption === PlaygroundContentOption.CHAT}
            >
              Chat
            </SegmentedControl.Button>
            <SegmentedControl.Button
              sx={smallSizeForSegmentControl}
              selected={selectedOption === PlaygroundContentOption.CODE}
            >
              Code
            </SegmentedControl.Button>
            <SegmentedControl.Button
              sx={smallSizeForSegmentControl}
              selected={selectedOption === PlaygroundContentOption.JSON}
            >
              Raw
            </SegmentedControl.Button>
          </SegmentedControl>

          <Box sx={{display: 'flex', gap: 2}}>
            {renderToolbar(selectedOption)}
            {onComparisonMode ? (
              <>
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton icon={InfoIcon} size="small" aria-label="Show model info" />
                  </ActionMenu.Anchor>
                  <ActionMenu.Overlay width="xlarge">
                    <ActionList>
                      <div className="border-bottom borderColor-default px-3 pt-2 pb-3">
                        <Text as="h1" sx={{fontSize: 1, fontWeight: 'bold', flex: 1}}>
                          Details
                        </Text>
                      </div>
                      <ModelInfo model={catalogData} />
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>

                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton icon={SlidersIcon} size="small" aria-label="Show parameters setting" />
                  </ActionMenu.Anchor>
                  <ActionMenu.Overlay width="xlarge">
                    <ActionList>
                      <div
                        className="d-flex flex-items-center border-bottom borderColor-default px-3 py-2"
                        style={{minHeight: '49px'}} // This is to prevent the height from changing when the reset button appears
                      >
                        <Text as="h1" sx={{fontSize: 1, fontWeight: 'bold', flex: 1}}>
                          Parameters
                        </Text>
                        <Box sx={{display: 'flex'}}>
                          {parametersHasChanges && (
                            <IconButton
                              icon={TrashIcon}
                              as="button"
                              variant="invisible"
                              disabled={!parametersHasChanges}
                              aria-label="Reset to default inputs"
                              onClick={() => manager.resetAllInputs(position, model)}
                            />
                          )}
                        </Box>
                      </div>
                      <ModelParameters model={model} position={position} />
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              </>
            ) : (
              <>
                {!showParameters && (
                  <IconButton
                    icon={SidebarExpandIcon}
                    size="small"
                    aria-label="Show parameters setting"
                    onClick={() => {
                      manager.setShowParameters(true)
                    }}
                  />
                )}
                <Box sx={{display: ['flex', 'flex', 'none'], gap: 2}}>
                  <IconButton
                    icon={InfoIcon}
                    size="small"
                    aria-label="Show model info"
                    onClick={() => {
                      manager.setTab(SidebarSelectionOptions.DETAILS)
                      manager.setShowParametersOnMobile(true)
                    }}
                  />
                  <IconButton
                    icon={SlidersIcon}
                    size="small"
                    aria-label="Show parameters setting"
                    onClick={() => {
                      manager.setTab(SidebarSelectionOptions.PARAMETERS)
                      manager.setShowParametersOnMobile(true)
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {renderOption(selectedOption)}
    </div>
  )
}

try{ RestoreHistoryButton.displayName ||= 'RestoreHistoryButton' } catch {}
try{ AddModel.displayName ||= 'AddModel' } catch {}
try{ PlaygroundChat.displayName ||= 'PlaygroundChat' } catch {}